import { useState, useEffect, useRef, useCallback } from 'react';

const CHARACTERS_PER_FRAME = 2;

export interface UseTypewriterOptions {
  /**
   * Whether to start the typewriter effect immediately
   * @default true
   */
  autoStart?: boolean;
  /**
   * Whether to support continuous streaming updates
   * @default false
   */
  supportStreaming?: boolean;
  /**
   * Resume animation from specific position
   */
  resumeFromPosition?: number;
  /**
   * Callback for progress updates (character index)
   */
  onProgressUpdate?: (characterIndex: number) => void;
}

export const useTypewriter = (text: string, options: UseTypewriterOptions = {}) => {
  const {
    autoStart = true,
    supportStreaming = false,
    resumeFromPosition = 0,
    onProgressUpdate,
  } = options;

  const [displayedText, setDisplayedText] = useState(autoStart ? '' : text);
  const [isComplete, setIsComplete] = useState(!autoStart);
  const animationRef = useRef<number>();
  const targetTextRef = useRef(text);
  const isTypingRef = useRef(false);

  const getInitialIndex = useCallback(() => {
    return Math.min(resumeFromPosition, text.length);
  }, [resumeFromPosition, text.length]);

  const indexRef = useRef(autoStart ? getInitialIndex() : text.length);

  const cleanup = useCallback(() => {
    if (animationRef.current !== undefined) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
  }, []);

  const finishTyping = useCallback(() => {
    cleanup();
    isTypingRef.current = false;
    setIsComplete(true);
  }, [cleanup]);

  const startTypewriter = useCallback(() => {
    if (!targetTextRef.current || isTypingRef.current) return;
    isTypingRef.current = true;
    setIsComplete(false);

    if (!supportStreaming && resumeFromPosition === 0) {
      setDisplayedText('');
      indexRef.current = 0;
    } else if (resumeFromPosition > 0) {
      indexRef.current = resumeFromPosition;
      setDisplayedText(targetTextRef.current.slice(0, resumeFromPosition));
    }

    const typeNextChar = () => {
      const currentTarget = targetTextRef.current;
      if (indexRef.current < currentTarget.length) {
        const newIndex = Math.min(indexRef.current + CHARACTERS_PER_FRAME, currentTarget.length);
        setDisplayedText(currentTarget.slice(0, newIndex));
        indexRef.current = newIndex;
        onProgressUpdate?.(newIndex);
        animationRef.current = requestAnimationFrame(typeNextChar);
      } else if (supportStreaming && currentTarget !== targetTextRef.current) {
        animationRef.current = requestAnimationFrame(typeNextChar);
      } else {
        finishTyping();
      }
    };

    typeNextChar();
  }, [supportStreaming, finishTyping, resumeFromPosition, onProgressUpdate]);

  // Handle text changes
  useEffect(() => {
    targetTextRef.current = text;

    if (supportStreaming) {
      // In streaming mode, start only if not already typing
      if (!isTypingRef.current && autoStart && text) {
        setTimeout(startTypewriter, 10);
      } else if (!autoStart && !isTypingRef.current) {
        // If autoStart is false and not currently typing, show text immediately
        setDisplayedText(text);
        setIsComplete(true);
        indexRef.current = text.length;
      }
      // Note: In streaming mode, if already typing, let the animation continue
    } else {
      // Non-streaming mode: reset and restart
      cleanup();
      isTypingRef.current = false;

      if (resumeFromPosition === 0) {
        indexRef.current = 0;
      }

      if (autoStart && text) {
        if (resumeFromPosition === 0) {
          setDisplayedText('');
        }
        setIsComplete(false);
        setTimeout(startTypewriter, 10);
      } else {
        // If autoStart is false, show text immediately
        setDisplayedText(text);
        setIsComplete(true);
        indexRef.current = text.length;
      }
    }
  }, [text, autoStart, supportStreaming, startTypewriter, cleanup, resumeFromPosition]);

  useEffect(() => cleanup, [cleanup]);

  return {
    displayedText,
    isComplete,
  };
};
